import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { getPatientUnpaidInvoices } from '@state/reducers/invoiceReducer'
import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import moment from 'moment'
import 'moment/locale/fr'

type Props = {
  patientId: number
}

export default function UnpaidPatientInvoices({ patientId }: Props) {
  const dispatch = useAppDispatch()
  const { invoices } = useAppSelector((state) => ({
    invoices: state.invoice.patientUnpaidInvoices || [],
  }))
  const [fetching, setFetching] = useState(false)

  useEffect(() => {
    let active = true
    const run = async () => {
      if (!patientId) return
      setFetching(true)
      try {
        await dispatch(getPatientUnpaidInvoices({ patientId })).unwrap()
      } finally {
        if (active) setFetching(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [dispatch, patientId])

  const fmt = (v: number | null | undefined) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(v ?? 0))

  const totals = useMemo(() => {
    let totalDue = 0
    let totalPaid = 0
    let totalAmoPaid = 0
    let totalAmcPaid = 0
    invoices.forEach((inv) => {
      totalDue += Number(inv.patient_total || 0)
      totalPaid += Number(inv.patient_payed || 0)
      totalAmoPaid += Number(inv.amo_payed || 0)
      totalAmcPaid += Number(inv.amc_payed || 0)
    })
    return {
      totalDue,
      totalPaid,
      outstanding: totalDue - totalPaid,
      totalAmoPaid,
      totalAmcPaid,
    }
  }, [invoices])

  return (
    <Stack spacing={2} sx={{ mt: 0.5 }}>
      {fetching && <LinearProgress />}
      {!fetching && (
        <Stack
          spacing={0.5}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">Résumé</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`À payer: ${fmt(totals.totalDue)}`}
              color="default"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Payé: ${fmt(totals.totalPaid)}`}
              color="success"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Reste: ${fmt(totals.outstanding)}`}
              color={totals.outstanding > 0 ? 'error' : 'success'}
              variant="filled"
            />
          </Stack>
        </Stack>
      )}

      {!fetching && invoices.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Aucune facture impayée.
        </Typography>
      )}

      {!fetching && invoices.length > 0 && (
        <List dense sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
          {invoices.map((inv, idx) => {
            const outstanding =
              Number(inv.patient_total || 0) - Number(inv.patient_payed || 0)
            return (
              <Box key={inv.id} sx={{ mb: 1.25 }}>
                <ListItem
                  disableGutters
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    bgcolor: 'grey.50',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                  >
                    <Typography variant="subtitle2">
                      Facture #{inv.id} {inv.orderId ? `(Exam #${inv.orderId})` : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: outstanding > 0 ? 'error.main' : 'success.main',
                      }}
                    >
                      Reste: {fmt(outstanding)}
                    </Typography>
                  </Stack>
                  <Table size="small" sx={{ mt: 0.75 }}>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            py: 0.5,
                            pr: 1.5,
                            borderBottom: 'none',
                            color: 'text.secondary',
                          }}
                        >
                          Date
                        </TableCell>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          {inv.date
                            ? moment(inv.date).format('DD/MM/YYYY')
                            : '-'}
                        </TableCell>
                      </TableRow>
                      {inv.examLabels?.length ? (
                        <TableRow>
                          <TableCell
                            sx={{
                              py: 0.5,
                              pr: 1.5,
                              borderBottom: 'none',
                              color: 'text.secondary',
                              verticalAlign: 'top',
                            }}
                          >
                            Procédures
                          </TableCell>
                          <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}
                            >
                              {inv.examLabels.join(', ')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      <TableRow>
                        <TableCell
                          sx={{
                            py: 0.5,
                            pr: 1.5,
                            borderBottom: 'none',
                            color: 'text.secondary',
                          }}
                        >
                          À payer
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.5,
                            borderBottom: 'none',
                            fontWeight: 600,
                          }}
                        >
                          {fmt(inv.patient_total)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            py: 0.5,
                            pr: 1.5,
                            borderBottom: 'none',
                            color: 'text.secondary',
                          }}
                        >
                          Payé
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.5,
                            borderBottom: 'none',
                            fontWeight: 600,
                          }}
                        >
                          {fmt(inv.patient_payed)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            py: 0.5,
                            pr: 1.5,
                            borderBottom: 'none',
                            color: 'text.secondary',
                          }}
                        >
                          AMO payé
                        </TableCell>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          {fmt(inv.amo_payed as any)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            py: 0.5,
                            pr: 1.5,
                            borderBottom: 'none',
                            color: 'text.secondary',
                          }}
                        >
                          AMC payé
                        </TableCell>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          {fmt(inv.amc_payed as any)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </ListItem>
                {idx < invoices.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            )
          })}
        </List>
      )}
    </Stack>
  )
}
